<script lang="ts">
    import { onDestroy, onMount, tick } from 'svelte'
    import { fade } from 'svelte/transition'

    import { isEqual } from 'lodash-es'
    import { toUnicode } from 'punycode-esm'

    import { gettext as _ } from 'hydria/services/gettext'

    import type { INameserver } from './order'

    interface Props {
        value: INameserver[]
        save: (value: string[]) => void
        close: () => void
    }

    let { value, save, close }: Props = $props()

    let orig: string[] = $state([])
    let edit: string[] = $state([])
    let editClean: string[] = $derived(edit.filter((v) => v.trim()))
    let ready = $state(false)

    function importValue(value: INameserver[]) {
        edit = value.map((ns) => ns.u_name)
        edit.sort((a, b) => a.localeCompare(b))
        orig = [...edit]
    }

    function clean(ns: string) {
        return toUnicode(ns.trim()).toLocaleLowerCase()
    }
    function cleanList(nameservers: string[]) {
        const set = new Set(nameservers.map((v) => clean(v)))
        const lst = [...set].filter((ns) => ns && ns.length)
        lst.sort((a, b) => a.localeCompare(b))

        return lst
    }

    function cleanupEdit(nameservers?: string[]) {
        if (nameservers == null) nameservers = [...edit]
        let lst = cleanList(nameservers)
        if (lst.length < 2) {
            while (lst.length < 2) {
                lst.push('')
            }
        } else if (lst.length < 6 && lst.slice(-1)[0] != '') {
            lst.push('')
        }

        edit = [...lst]
        tick().then(() => {
            for (const [i, v] of edit.entries()) {
                if (!v) {
                    const el = document.getElementById(`ecom-ns-${i}`)
                    if (el) el.focus()
                    break
                }
            }
        })
    }

    function isLocalValid(value: string) {
        if (!ready) return
        let lvalue = clean(value)
        return !lvalue || lvalue.length == 0 || (lvalue.length <= 50 && lvalue.match(/^[^\. ]+(\.[^\. ]+)+$/))
    }

    function isValid(nameservers: string[]) {
        if (!ready) return false
        const lst = cleanList(nameservers)
        const notOk = lst.filter((v) => !isLocalValid(v)).length
        return (!notOk && lst.length >= 2 && lst.length <= 6) || lst.length == 0
    }

    function onBlur() {
        cleanupEdit()
    }

    onMount(() => {
        importValue(value)
        cleanupEdit()
        ready = true
    })

    onMount(() => {
        if (!document.documentElement.classList.contains('modal-open')) document.documentElement.classList.add('modal-open')
    })
    onDestroy(() => {
        if (document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            close()
        } else if ((e.ctrlKey || e.shiftKey) && e.key == 'Enter' && isValid(edit)) {
            if (isEqual(editClean, orig)) close()
            else save(cleanList(edit))
        }
    }}
/>

<div class="modal modal-ds-records" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog">
        <div class="modal-content">
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    save(cleanList(edit))
                }}
            >
                <div class="modal-header text-bg-primary">
                    <div>{$_('Serveurs DNS')}</div>
                </div>
                <div class="modal-body">
                    <div class="border-group">
                        {#if ready}
                            <div class="form-horizontal d-grid gap-2">
                                {#each edit as entry, i}
                                    <div class="row">
                                        <label for="ecom-ns-{i}" class="col-sm-3 col-form-label">{$_('Serveur DNS {n}', { n: i + 1 })}</label>
                                        <div class="col-sm-9">
                                            <input
                                                type="text"
                                                class="form-control"
                                                id="ecom-ns-{i}"
                                                class:is-invalid={!isLocalValid(entry)}
                                                bind:value={edit[i]}
                                                onblur={() => onBlur()}
                                                onkeyup={(e) => {
                                                    if (e.key == 'Enter') onBlur()
                                                }}
                                            />
                                        </div>
                                    </div>
                                {/each}
                            </div>
                        {/if}
                    </div>
                </div>
                <div class="modal-footer">
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={() => {
                            close()
                        }}>{$_('Cancel')}</button
                    >
                    <button class="btn btn-primary" type="submit" disabled={!isValid(edit) || isEqual(editClean, orig)}>{$_('Save')}</button>
                </div>
            </form>
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            close()
        }}
        aria-hidden="true"
    ></div>
</div>
